import { Checkbox } from "@certa/catalyst/components/Checkbox";
import { classNames } from "@certa/catalyst/utils/common";
import { Radio } from "@certa/catalyst/components/Radio";

import { useInternalTableDndSortable } from "../InternalTableDnd/hooks/useInternalTableDndSortable";
import { CSS } from "../InternalTableDnd/InternalTableDnd.utils";

import { flexRender } from "../utils/render";
import styles from "./InternalTableCell.module.css";

import { getCommonPinningStyles } from "../InternalTableHeader/InternalTableHeaderPinner/InternalTableHeaderPinner.utils";

import { SELECT_ROW_COLUMN_ID, EXPAND_ROW_COLUMN_ID } from "../Table.constants";

import type { Cell, InternalRowData } from "../Table.types";

import tableStyles from "../Table.module.css";
import { ChevronRight } from "@certa/icons/components/ChevronRight";
import { ChevronDown } from "@certa/icons/components/ChevronDown";

type InternalTableCellProps<TableData> = {
  cell: Cell<TableData, unknown>;
  onRowSelect?: (rowId: string) => void;
  onRowExpand?: (rowId: string) => void;
  expandableRowId: string;
  canShowSelectionColumn?: boolean;
};

export const InternalTableCell = <TableData extends InternalRowData>(
  props: InternalTableCellProps<TableData>
) => {
  const {
    cell,
    onRowSelect,
    onRowExpand,
    expandableRowId,
    canShowSelectionColumn = false
  } = props;

  const { isDragging, setNodeRef, transform, transition } =
    useInternalTableDndSortable(cell.column);

  const canShowRowSelectionCell =
    cell.column.id === SELECT_ROW_COLUMN_ID &&
    (canShowSelectionColumn ? true : cell.row.getCanSelect());

  const canShowRowExpandCell =
    cell.column.id === EXPAND_ROW_COLUMN_ID && cell.row.getCanExpand();

  const columnPinningStyles = getCommonPinningStyles(cell.column);

  const isResizing = cell.column.getIsResizing();

  // TODO: Remove this once we migrate to the new cell renderer
  const LegacyCellRenderer = cell.renderValue() as React.ReactNode;

  if (canShowRowExpandCell) {
    const isRowExpanded = cell.row.getIsExpanded();

    const handleOnRowExpand = () => {
      cell.row.getToggleExpandedHandler()();
      onRowExpand?.(cell.row.id);
    };

    const getExpandedRowLabel = () => {
      const expandedRowLabel = cell.row.original?.subExpandedRowLabel;

      if (isRowExpanded) {
        return expandedRowLabel + " row expanded";
      }

      return expandedRowLabel + " row collapsed";
    };

    return (
      <td
        key={cell.id}
        className={tableStyles.tableCell}
        style={{
          ...columnPinningStyles,
          boxShadow: "none"
        }}
      >
        <button
          onClick={handleOnRowExpand}
          data-testid={`expand-row-button-${cell.row.id}`}
          className={styles.expandRowButton}
          // Note: aria-expanded is not added because expanded state is manually
          // handled below.
          aria-controls={expandableRowId}
        >
          <span aria-hidden={true} style={{ height: 16 }}>
            {isRowExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </span>
          <div className="catalyst-screen-reader-only">
            {getExpandedRowLabel()}
          </div>
        </button>
      </td>
    );
  }

  if (canShowRowSelectionCell) {
    const canSelect = cell.row.getCanSelect();

    const handleOnSelectMultiRow = (
      _: boolean,
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      cell.row.getToggleSelectedHandler()(evt);
      onRowSelect?.(cell.row.id);
    };

    const handleOnSelectSingleRow = (
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      cell.row.getToggleSelectedHandler()(evt);
      onRowSelect?.(cell.row.id);
    };

    const isMultiSelect = cell.row.getCanMultiSelect();

    if (isMultiSelect) {
      return (
        <td
          key={cell.id}
          className={tableStyles.tableCell}
          style={{
            ...columnPinningStyles,
            width: cell.column.getSize(),
            boxShadow: "none"
          }}
        >
          <Checkbox
            aria-label="Select row"
            value={cell.row.id}
            // When multi-row selection is disabled, we still show the indeterminate state
            // to indicate partial selection. While row expansion typically shows sub-rows,
            // we use it for custom content instead. So the indeterminate state is also used to
            // indicate that the row is actually selected.
            checked={cell.row.getIsSelected() || cell.row.getIsSomeSelected()}
            onChange={handleOnSelectMultiRow}
            disabled={!canSelect}
          />
        </td>
      );
    }

    return (
      <td
        key={cell.id}
        className={tableStyles.tableCell}
        style={{
          ...columnPinningStyles,
          width: cell.column.getSize(),
          boxShadow: "none"
        }}
      >
        <Radio
          label=""
          value={cell.row.id}
          // When multi-row selection is disabled, we still show the indeterminate state
          // to indicate partial selection. While row expansion typically shows sub-rows,
          // we use it for custom content instead. So the indeterminate state is also used to
          // indicate that the row is actually selected.
          checked={cell.row.getIsSelected() || cell.row.getIsSomeSelected()}
          onChange={handleOnSelectSingleRow}
          disabled={!canSelect}
        />
      </td>
    );
  }

  return (
    <td
      key={cell.id}
      ref={setNodeRef}
      className={classNames({
        [tableStyles.tableCell]: true,
        [tableStyles.tableCellDragging]: isDragging,
        [tableStyles.tableCellResizing]: isResizing
      })}
      style={{
        opacity: isDragging ? 0.8 : 1,
        position: "relative",
        // translate instead of transform to avoid squishing
        transform: CSS.Translate.toString(transform),
        transition: transition ?? "",
        width: cell.column.getSize(),
        ...columnPinningStyles,
        zIndex: isDragging ? 1 : columnPinningStyles.zIndex
      }}
    >
      {cell.column.columnDef.cell
        ? flexRender(cell.column.columnDef.cell, cell.getContext())
        : LegacyCellRenderer}
    </td>
  );
};
